import axios from 'axios';
import { getFeaturedChurchUpdates, getStories } from '@/utils/storyblok';
import { getErrorMessage } from '@/utils/error';
import { canShareChurchUpdate, normalizeChurchUpdateShareUrl } from '@/utils/church-updates';

/**
 * Extract plain text from Storyblok rich text field
 * Handles both rich text objects and plain strings
 * @param {string|Object} richText - Storyblok rich text field (can be string or object)
 * @returns {string} Plain text extracted from rich text
 */
function extractTextFromRichText(richText) {
  if (!richText) return '';

  // If it's already a string, return it
  if (typeof richText === 'string') {
    // Strip HTML tags if present
    return richText
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // If it's an object (Storyblok rich text format)
  if (typeof richText === 'object' && richText !== null) {
    // Handle Storyblok rich text format with content array
    if (Array.isArray(richText.content)) {
      return richText.content
        .map(block => {
          if (block.content && Array.isArray(block.content)) {
            return block.content.map(text => text.text || '').join('');
          }
          return block.text || '';
        })
        .join(' ')
        .trim();
    }
    // Handle other object formats
    if (richText.text) return richText.text;
    if (richText.content) return String(richText.content);
  }

  return '';
}

const INITIAL_STATE = {
  isLoading: false,
  selectedArticleIndex: 0,
  churchUpdates: [],
  featuredChurchUpdates: [],
};

const state = () => INITIAL_STATE;

/**
 * Check if current route is a preview route
 * Preview routes follow pattern: /preview/:id/...
 * @returns {boolean} True if current path is a preview route
 */
function isPreviewRoute() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    return window.location.pathname.startsWith('/preview/');
  } catch (_) {
    return false;
  }
}

const mutations = {
  // set updates
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setChurchUpdates(state, churchUpdates) {
    state.churchUpdates = churchUpdates;
  },
  setFeaturedChurchUpdates(state, featuredChurchUpdates) {
    state.featuredChurchUpdates = featuredChurchUpdates;
  },
};

const actions = {
  // fetch churchupdates
  // - Preview mode: Storyblok direct (draft content, no PAC filtering)
  // - Published mode: API (Storyblok content with backend PAC filtering)
  async getChurchUpdates({ commit, dispatch, rootState }) {
    try {
      commit('setIsLoading', true);

      const isPreview = isPreviewRoute();

      if (isPreview) {
        // Preview mode: Use Storyblok direct (draft content, no PAC)
        // This allows CMS editors to preview draft content before publishing
        try {
          const stories = await getStories({
            contentType: 'Church Update Page',
            perPage: 50,
            version: 'draft', // Get draft content for preview
          });

          // Map Storyblok stories to app format
          const mappedUpdates = stories.map(story => {
            const content = story.content || {};
            const thumbnail = content.thumbnail || {};

            return {
              slug: content.slug || story.slug || story.full_slug || '',
              title: content.title || story.name || '',
              date: content.date || story.first_published_at || story.published_at || '',
              description: extractTextFromRichText(content.description),
              thumbnail: {
                // Prefer external URL text field (e.g. S3) over Storyblok-hosted asset.
                url: content.thumbnailUrl?.trim() || thumbnail.filename || thumbnail.url || '',
              },
              isExternalLink: content.isExternalLink || false,
              showShareButton: canShareChurchUpdate(content),
              church_update_tags: content.church_update_tags || [],
            };
          });

          // Sort by date field (descending)
          mappedUpdates.sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            return dateB - dateA;
          });

          commit('setChurchUpdates', mappedUpdates);
          commit('setIsLoading', false);
          return;
        } catch (storyblokError) {
          console.error('[ChurchUpdates Preview] Storyblok fetch failed:', storyblokError);
          commit('setChurchUpdates', []);
          commit('setIsLoading', false);
          return;
        }
      }

      // Published mode: Use API (backend fetches from Storyblok with PAC filtering)
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + '/churchupdates/get-updates',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting church updates');
      commit('setChurchUpdates', data);
      commit('setIsLoading', false);
    } catch (e) {
      commit('setIsLoading', false);
      const errorMessage = getErrorMessage(
        e,
        'Failed to get church updates. Please try again later.',
      );
      dispatch('toast/error', errorMessage, {
        root: true,
      });
    }
  },

  async generateShareLink({ rootState }, { slug }) {
    const accessToken = rootState.user.token;
    const { data } = await axios.post(
      process.env.VUE_APP_API_URL + '/deeplink/share-link',
      { path: `/church-updates/${slug}` },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return normalizeChurchUpdateShareUrl(data.shortUrl);
  },

  async getUpdateBySlug({ rootState }, { slug }) {
    const accessToken = rootState.user.token;
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/churchupdates/by-slug/${encodeURIComponent(slug)}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return data;
  },

  async getBodyBySlug({ rootState }, { slug }) {
    const accessToken = rootState.user.token;
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/churchupdates/body/${encodeURIComponent(slug)}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return Array.isArray(data?.body) ? data.body : [];
  },

  async getFeaturedChurchUpdates({ commit, dispatch, rootState }, payload) {
    try {
      commit('setIsLoading', true);

      // payload can be options object or undefined
      const options = payload && typeof payload === 'object' ? payload : {};
      const skipStoryblok = options.skipStoryblok === true;
      const isPreview = isPreviewRoute();

      if (isPreview && !skipStoryblok) {
        // Preview mode: Use Storyblok direct (draft content, no PAC)
        try {
          console.log('[ChurchUpdates Preview] Fetching featured updates from Storyblok...');
          const featuredUpdates = await getFeaturedChurchUpdates();

          // Format to match API response structure: [{ update: [...] }]
          const formattedData = featuredUpdates.length > 0 ? [{ update: featuredUpdates }] : [];

          commit('setFeaturedChurchUpdates', formattedData);
          commit('setIsLoading', false);
          return;
        } catch (storyblokError) {
          console.error('[ChurchUpdates Preview] Storyblok fetch failed:', storyblokError);
          commit('setFeaturedChurchUpdates', []);
          commit('setIsLoading', false);
          return;
        }
      }

      // Published mode: Use API (backend fetches from Storyblok with PAC filtering)
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + '/churchupdates/get-featured-updates',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting featured church updates');
      commit('setFeaturedChurchUpdates', data);
      commit('setIsLoading', false);
    } catch (e) {
      commit('setIsLoading', false);
      commit('setFeaturedChurchUpdates', []);
      const errorMessage = getErrorMessage(
        e,
        'Failed to get featured church updates. Please try again later.',
      );
      dispatch('toast/error', errorMessage, {
        root: true,
      });
    }
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
